import { useState, useEffect, useCallback } from 'react';
import { X, Send, Calendar, Loader2, Users, User, Shield, Globe, Mail, Clock, Bell, ImagePlus } from 'lucide-react';
import ReactSelect, { StylesConfig, GroupBase } from 'react-select';
import { useAdminNotifications } from '@/hooks/useNotifications';
import {
  NotificationType,
  NotificationTypeConfig,
  NotificationPriority,
  NotificationPriorityConfig,
  NotificationChannel,
  NotificationTargetType,
  CreateNotificationRequest,
  PushOptions,
} from '@/types/notification.types';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api';
import { API_ENDPOINTS } from '@/config/api.config';
import SimpleSelect, { SelectOption } from '@/components/ui/SimpleSelect';
import { useDebounce } from '@/utils/debounce';
import EmailComposePanel, { EmailComposeData, EmailRecipient } from './EmailComposePanel';
import { fileService } from '@/services/file.service';
import toast from 'react-hot-toast';
import { cleanText } from '@/utils/sanitize';
import { useForm } from 'react-hook-form';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';

// Free-text fields rendered with the shared Input/Textarea (VAPT #12/#13 clean-text rule)
interface NotificationTextFields {
  title: string;
  message: string;
  action_url: string;
}

const EMPTY_TEXT_FIELDS: NotificationTextFields = { title: '', message: '', action_url: '' };

interface SendNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface MultiSelectOption {
  label: string;
  value: string;
}

// Build select options for type and priority
const typeSelectOptions: SelectOption[] = Object.entries(NotificationTypeConfig).map(
  ([type, config]) => ({ label: config.label, value: type })
);

const prioritySelectOptions: SelectOption[] = Object.entries(NotificationPriorityConfig).map(
  ([priority, config]) => ({ label: config.label, value: priority })
);

// Push TTL unit options for the OneSignal-style "value + unit" input.
// We pair these with a number input so admins can express any TTL within
// the [60s, 2,419,200s (4 weeks)] envelope FCM/WNS will queue.
type TtlUnit = 'minutes' | 'hours' | 'days' | 'weeks';
const ttlUnitOptions: SelectOption[] = [
  { label: 'minutes', value: 'minutes' },
  { label: 'hours', value: 'hours' },
  { label: 'days', value: 'days' },
  { label: 'weeks', value: 'weeks' },
];
const ttlUnitSeconds: Record<TtlUnit, number> = {
  minutes: 60,
  hours: 3600,
  days: 86400,
  weeks: 604800,
};
const PUSH_TTL_MIN_SECONDS = 60;
const PUSH_TTL_MAX_SECONDS = 2419200; // 4 weeks
const PUSH_TTL_DEFAULT_SECONDS = 259200; // 3 days — OneSignal default

// Styles for multi-select (react-select)
const multiSelectStyles: StylesConfig<MultiSelectOption, true, GroupBase<MultiSelectOption>> = {
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused ? '#4f46e5' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(79, 70, 229, 0.1)' : 'none',
    '&:hover': { borderColor: '#4f46e5' },
    minHeight: '42px',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#4f46e5' : state.isFocused ? '#eef2ff' : 'white',
    color: state.isSelected ? 'white' : '#1f2937',
    cursor: 'pointer',
    fontSize: '0.875rem',
    padding: '0.5rem 0.75rem',
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    zIndex: 9999,
  }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  menuList: (provided) => ({ ...provided, padding: '0.25rem', maxHeight: '200px' }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: '#eef2ff',
    borderRadius: '0.375rem',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: '#4338ca',
    fontSize: '0.8rem',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: '#4338ca',
    '&:hover': { backgroundColor: '#c7d2fe', color: '#3730a3' },
  }),
  placeholder: (provided) => ({ ...provided, color: '#9ca3af', fontSize: '0.875rem' }),
};

export function SendNotificationModal({
  isOpen,
  onClose,
  onSuccess,
}: SendNotificationModalProps) {
  const { sendNotification, scheduleNotification } = useAdminNotifications();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [emailCompose, setEmailCompose] = useState<EmailComposeData>({
    email_subject: '',
    email_html_body: '',
    cc_emails: [],
    bcc_emails: [],
  });
  // Recipients selected via email To field (users + groups + roles)
  const [emailRecipients, setEmailRecipients] = useState<EmailRecipient[]>([]);

  // Push-channel customization (only applied when PUSH is in channels)
  const [pushOptions, setPushOptions] = useState<PushOptions>({
    image_url: '',
    icon_url: '',
    ttl: PUSH_TTL_DEFAULT_SECONDS, // 3 days — OneSignal default
    silent: false,
  });

  // OneSignal-style TTL editor: a numeric input + unit dropdown. These two
  // are the source of truth for what the admin sees; we project them down
  // to a single seconds value that lands in pushOptions.ttl ONLY when the
  // pair validates. If the admin types an out-of-range value (e.g. 5 weeks,
  // 100 days, 0 minutes), we surface an inline error and pause writing the
  // seconds value through — the admin sees exactly what they typed and a
  // red message telling them what's wrong, instead of the input silently
  // snapping to a different number.
  const [ttlValue, setTtlValue] = useState<number>(3);
  const [ttlUnit, setTtlUnit] = useState<TtlUnit>('days');
  const [ttlError, setTtlError] = useState<string | null>(null);

  // Per-field inline errors for required fields. Replaces the browser's
  // default `required` tooltip with red <p> messages rendered below each
  // input (same pattern used by the TTL field). Errors are set during
  // handleSubmit validation and cleared automatically as the user types.
  const [formErrors, setFormErrors] = useState<{
    title?: string;
    message?: string;
    scheduled_at?: string;
    action_url?: string;
    email_subject?: string;
  }>({});

  // Maximum integer the admin can express in the value input for a given unit.
  // 4 weeks = 28 days = 672 hours = 40 320 minutes is the FCM/WNS ceiling.
  const maxValueForUnit = (unit: TtlUnit): number =>
    Math.floor(PUSH_TTL_MAX_SECONDS / ttlUnitSeconds[unit]);

  // Returns an error string if (value, unit) is out of the FCM/WNS envelope,
  // null if valid. Centralizes the bounds check so the live onChange path,
  // the unit-switch path, and the submit guard all agree on what's invalid.
  const validateTtl = (value: number, unit: TtlUnit): string | null => {
    if (!Number.isFinite(value) || value < 1) {
      return 'Enter a number of at least 1.';
    }
    const max = maxValueForUnit(unit);
    if (value > max) {
      return `Maximum is ${max.toLocaleString()} ${unit} (4 weeks total).`;
    }
    return null;
  };

  // Apply a new (value, unit) pair. Always updates the display state so the
  // admin sees exactly what they typed. Only flows through to pushOptions.ttl
  // when validation passes — invalid input leaves the last good seconds value
  // in place, and we rely on the submit guard to block sends.
  const updateTtl = (nextValue: number, nextUnit: TtlUnit) => {
    setTtlValue(nextValue);
    setTtlUnit(nextUnit);
    const error = validateTtl(nextValue, nextUnit);
    setTtlError(error);
    if (!error) {
      const seconds = Math.round(nextValue * ttlUnitSeconds[nextUnit]);
      setPushOptions((prev) => ({ ...prev, ttl: seconds }));
    }
  };

  // Tracks which push-image field is uploading (image_url / icon_url)
  const [uploadingField, setUploadingField] = useState<keyof PushOptions | null>(null);

  const handlePushImageUpload = async (
    field: 'image_url' | 'icon_url',
    file: File
  ) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setUploadingField(field);
    try {
      const data = await fileService.uploadFile(file);
      const url = data?.url;
      if (!url) throw new Error('Upload returned no URL');
      setPushOptions((prev) => ({ ...prev, [field]: url }));
      toast.success('Uploaded');
    } catch (err: any) {
      toast.error(err?.message || 'Upload failed');
    } finally {
      setUploadingField(null);
    }
  };

  // Form state
  const [formData, setFormData] = useState<{
    type: NotificationType;
    priority: NotificationPriority;
    channels: NotificationChannel[];
    target_type: NotificationTargetType;
    target_ids: string[];
    scheduled_at: string;
  }>({
    type: NotificationType.ANNOUNCEMENT,
    priority: NotificationPriority.NORMAL,
    channels: [NotificationChannel.IN_APP],
    target_type: NotificationTargetType.ALL,
    target_ids: [],
    scheduled_at: '',
  });

  // Title, message and action URL live in react-hook-form so the shared fields validate them
  const {
    control: textControl,
    getValues: getTextValues,
    trigger: triggerTextFields,
    reset: resetTextFields,
    watch: watchTextFields,
  } = useForm<NotificationTextFields>({ mode: 'onChange', defaultValues: EMPTY_TEXT_FIELDS });
  const messageLength = (watchTextFields('message') || '').length;

  const isEmailActive = formData.channels.includes(NotificationChannel.EMAIL);

  // Cache of selected user options so they persist across search changes
  const [selectedUserCache, setSelectedUserCache] = useState<Map<string, MultiSelectOption>>(new Map());

  // Debounce user search so API is called only after user stops typing (400ms)
  const debouncedUserSearch = useDebounce(userSearch, 400);

  // Fetch all active users for dropdown (no pagination, dedicated endpoint)
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users-dropdown', debouncedUserSearch],
    queryFn: async () => {
      const response: any = await apiClient.get(API_ENDPOINTS.USERS.DROPDOWN, {
        params: debouncedUserSearch ? { search: debouncedUserSearch } : {},
      });
      const data = response.data?.data;
      return Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
    },
    enabled: isOpen && !isEmailActive && formData.target_type === NotificationTargetType.USER,
  });

  // Fetch groups for target selection. Also enabled when target_type is ROLE
  // because the cascading role picker uses this same dept (group) list as
  // its first-level select.
  const { data: groupsData, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['groups-dropdown'],
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.GROUPS.ALL);
      const data = response.data?.data;
      return Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
    },
    enabled:
      isOpen &&
      !isEmailActive &&
      (formData.target_type === NotificationTargetType.GROUP ||
        formData.target_type === NotificationTargetType.ROLE),
  });

  // Currently selected dept (group_key) in the ROLE cascade. Drives the
  // roles-for-dept query below. Empty means no dept picked yet — the role
  // dropdown is hidden in that state.
  const [selectedRoleDept, setSelectedRoleDept] = useState<string>('');

  // Picked (group_key, role_key) tuples for ROLE target. Each entry is the
  // notification's target for one specific role inside one specific dept;
  // the same role_key under a different dept is a separate entry.
  const [selectedRoleAssignments, setSelectedRoleAssignments] = useState<
    Array<{ group_key: string; role_key: string; role_name?: string; group_name?: string }>
  >([]);

  // Fetch roles for the currently selected dept. Mirrors the CourseAssignment
  // pattern — GET /group-role-map/{groupKey} returns the roles available for
  // that specific dept. Skipped until a dept is selected.
  const { data: rolesForDept, isLoading: isLoadingRolesForDept } = useQuery({
    queryKey: ['group-role-map', selectedRoleDept],
    queryFn: async () => {
      if (!selectedRoleDept) return [] as Array<{ role_key: string; role_name?: string }>;
      const response = await apiClient.get(`/group-role-map/${selectedRoleDept}`);
      const roles = response.data?.data?.roles;
      return Array.isArray(roles) ? roles : [];
    },
    enabled:
      isOpen &&
      !isEmailActive &&
      formData.target_type === NotificationTargetType.ROLE &&
      !!selectedRoleDept,
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        type: NotificationType.ANNOUNCEMENT,
        priority: NotificationPriority.NORMAL,
        channels: [NotificationChannel.IN_APP],
        target_type: NotificationTargetType.ALL,
        target_ids: [],
        scheduled_at: '',
      });
      resetTextFields(EMPTY_TEXT_FIELDS);
      setIsScheduled(false);
      setUserSearch('');
      setSelectedUserCache(new Map());
      setEmailCompose({
        email_subject: '',
        email_html_body: '',
        cc_emails: [],
        bcc_emails: [],
      });
      setEmailRecipients([]);
      setPushOptions({
        image_url: '',
        icon_url: '',
        ttl: PUSH_TTL_DEFAULT_SECONDS,
        silent: false,
      });
      setTtlValue(3);
      setTtlUnit('days');
      setTtlError(null);
      setFormErrors({});
      setSelectedRoleDept('');
      setSelectedRoleAssignments([]);
    }
  }, [isOpen]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear inline error for this field as the admin types so they aren't
    // staring at a stale red message after fixing the problem.
    if (name in formErrors) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleChannelToggle = (channel: NotificationChannel) => {
    // Single-select: clicking a channel selects only that one
    const was_active = formData.channels.includes(channel) && formData.channels.length === 1;

    // Don't allow deselecting the only active channel
    if (was_active) return;

    // Switching away from EMAIL — clear the email "To" recipients
    if (formData.channels.includes(NotificationChannel.EMAIL) && channel !== NotificationChannel.EMAIL) {
      setEmailRecipients([]);
    }

    setFormData((prev) => ({
      ...prev,
      channels: [channel],
      // When switching INTO email, reset the non-email target_type so it doesn't bleed over
      ...(channel === NotificationChannel.EMAIL
        ? { target_type: NotificationTargetType.ALL, target_ids: [] }
        : {}),
    }));
  };

  // Email "To" handlers
  const handleAddRecipient = useCallback((user: EmailRecipient) => {
    setEmailRecipients((prev) => {
      if (prev.some((r) => r.id === user.id)) return prev;
      return [...prev, user];
    });
  }, []);

  const handleRemoveRecipient = useCallback((userId: string) => {
    setEmailRecipients((prev) => prev.filter((r) => r.id !== userId));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.channels.length === 0) return;

    // Block submit when push is active and the TTL is invalid. The inline
    // error stays visible in the field; the toast nudges the admin's eye
    // toward it if they hit Send without noticing.
    if (formData.channels.includes(NotificationChannel.PUSH) && ttlError) {
      toast.error(ttlError);
      return;
    }

    // Validation — collect inline errors and set them all at once so the
    // admin sees every problem on the form instead of having to fix one,
    // submit, fix the next, submit, etc.
    const nextErrors: typeof formErrors = {};
    if (isEmailActive) {
      // EmailComposePanel is a sub-component that doesn't currently surface
      // inline errors per-field, so its validation stays as toasts to avoid
      // scope creep. The email_subject key is reserved for when that panel
      // is wired up to receive an error prop.
      if (!emailCompose.email_subject.trim()) {
        toast.error('Please fill in the email subject');
        return;
      }
      if (formData.target_type !== NotificationTargetType.ALL && emailRecipients.length === 0) {
        toast.error('Please add at least one recipient in the To field');
        return;
      }
    } else {
      // Title, message and action URL: the shared fields show their own errors (incl. clean-text)
      if (!(await triggerTextFields())) {
        return;
      }
      if (
        formData.target_type !== NotificationTargetType.ALL &&
        formData.target_type !== NotificationTargetType.ROLE &&
        formData.target_ids.length === 0
      ) {
        toast.error('Please select at least one recipient');
        return;
      }
      if (
        formData.target_type === NotificationTargetType.ROLE &&
        selectedRoleAssignments.length === 0
      ) {
        toast.error('Please select at least one department + role');
        return;
      }
    }
    if (isScheduled && !formData.scheduled_at) {
      nextErrors.scheduled_at = 'Please pick a date and time for the scheduled send';
    }

    // If any inline field has an error, surface them and bail before
    // hitting the API.
    if (Object.values(nextErrors).some(Boolean)) {
      setFormErrors(nextErrors);
      return;
    }
    setFormErrors({});

    setIsSubmitting(true);

    const textFields = getTextValues();
    const effectiveTitle = isEmailActive
      ? emailCompose.email_subject.trim()
      : textFields.title.trim();
    const effectiveMessage = isEmailActive
      ? (emailCompose.email_html_body.replace(/<[^>]*>/g, '').trim() || emailCompose.email_subject.trim())
      : textFields.message.trim();

    // target_ids resolution by target_type:
    //   - EMAIL mode: chips from the To field
    //   - ROLE: array of { group_key, role_key } tuples (cascading picker)
    //   - USER / GROUP: array of id/key strings (multi-select)
    // Backend's resolveTargetToUserIds branches on target_type and expects
    // each of these exact shapes.
    const email_target_ids: (string | { group_key: string; role_key: string })[] =
      isEmailActive
        ? emailRecipients.map((r) => r.id)
        : formData.target_type === NotificationTargetType.ROLE
        ? selectedRoleAssignments.map((a) => ({ group_key: a.group_key, role_key: a.role_key }))
        : formData.target_ids;

    // Strip empty strings / defaults from push options so backend doesn't
    // receive empty-string fields that fail OneSignal's URL validation.
    const isPushActive = formData.channels.includes(NotificationChannel.PUSH);
    const cleanedPushOptions: PushOptions = {};
    if (isPushActive) {
      if (pushOptions.image_url?.trim()) cleanedPushOptions.image_url = pushOptions.image_url.trim();
      if (pushOptions.icon_url?.trim()) cleanedPushOptions.icon_url = pushOptions.icon_url.trim();
      // Always persist TTL (in seconds) when push is active, even when the
      // admin kept the default 3 days. This ensures the value the recipient
      // sees in the notification list / push details modal matches what was
      // actually used for delivery, and gives us audit data for every push.
      if (pushOptions.ttl && pushOptions.ttl > 0) cleanedPushOptions.ttl = pushOptions.ttl;
      if (pushOptions.silent) cleanedPushOptions.silent = true;
    }

    const notificationData: CreateNotificationRequest = {
      title: cleanText(effectiveTitle),
      message: cleanText(effectiveMessage),
      type: formData.type,
      priority: formData.priority,
      channels: formData.channels,
      target_type: formData.target_type,
      target_ids:
        formData.target_type === NotificationTargetType.ALL ? undefined : email_target_ids,
      action_url: textFields.action_url.trim()
        ? `${window.location.origin}${textFields.action_url.trim()}`
        : undefined,
      ...(isPushActive && Object.keys(cleanedPushOptions).length > 0
        ? { push_options: cleanedPushOptions }
        : {}),
      ...(isEmailActive && emailCompose.email_subject.trim()
        ? { email_subject: cleanText(emailCompose.email_subject.trim()) }
        : {}),
      ...(isEmailActive && emailCompose.email_html_body.trim()
        ? { email_html_body: emailCompose.email_html_body }
        : {}),
      ...(isEmailActive && emailCompose.cc_emails.length > 0
        ? { cc_emails: emailCompose.cc_emails }
        : {}),
      ...(isEmailActive && emailCompose.bcc_emails.length > 0
        ? { bcc_emails: emailCompose.bcc_emails }
        : {}),
    };

    const result =
      isScheduled && formData.scheduled_at
        ? await scheduleNotification({
            ...notificationData,
            scheduled_at: new Date(formData.scheduled_at).toISOString(),
          })
        : await sendNotification(notificationData);

    setIsSubmitting(false);

    if (result.success) {
      onSuccess?.();
      onClose();
    }
  };

  // Build multi-select options for target selection (non-email mode)
  const getTargetOptions = useCallback((): MultiSelectOption[] => {
    if (formData.target_type === NotificationTargetType.USER) {
      const searchResults: MultiSelectOption[] = (usersData || []).map((item: any) => {
        const name = item.full_name || `${item.given_name || ''} ${item.family_name || ''}`.trim();
        const details = [item.email, item.employee_id].filter(Boolean).join(' | ');
        return {
          label: details ? `${name} (${details})` : name,
          value: item._id,
        };
      });
      // Merge cached selections so previously-selected users stay visible
      // even when the current search results don't include them.
      const resultIds = new Set(searchResults.map((o) => o.value));
      const cached = Array.from(selectedUserCache.values()).filter((o) => !resultIds.has(o.value));
      return [...cached, ...searchResults];
    }
    if (formData.target_type === NotificationTargetType.GROUP) {
      return (groupsData || []).map((item: any) => ({
        label: item.group_name || item.name,
        value: item.group_key || item._id,
      }));
    }
    // ROLE target type uses a separate cascading dept→role picker (rendered
    // below via selectedRoleAssignments). It does not flow through the flat
    // multi-select, so there's intentionally no ROLE branch here.
    return [];
  }, [formData.target_type, usersData, groupsData, selectedUserCache]);

  const targetOptions = getTargetOptions();
  const selectedTargetOptions = targetOptions.filter((opt) =>
    formData.target_ids.includes(opt.value)
  );

  const isLoadingTarget =
    (formData.target_type === NotificationTargetType.USER && isLoadingUsers) ||
    (formData.target_type === NotificationTargetType.GROUP && isLoadingGroups);

  const targetLabel =
    formData.target_type === NotificationTargetType.USER
      ? 'Users'
      : formData.target_type === NotificationTargetType.GROUP
      ? 'Departments'
      : 'Roles';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0  z-50 flex items-center justify-center bg-black/50 common-modal-overlay">
      <div className={`w-full max-h-[90vh] bg-white rounded-xl shadow-xl overflow-hidden transition-all duration-300 ${isEmailActive ? 'max-w-3xl' : 'max-w-2xl'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-xl">
              <Send className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isScheduled ? 'Schedule Notification' : 'Send Notification'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)] custom-scrollbar">
          <div className="p-6 space-y-6">
            {/* Title & Message - hidden when only EMAIL channel is selected */}
            {!isEmailActive && (
              <>
                {/* Title */}
                <Input<NotificationTextFields>
                  name="title"
                  control={textControl}
                  label="Title"
                  required
                  type="text"
                  placeholder="Enter notification title"
                  rules={{
                    validate: (value: string) => !!value?.trim() || 'Please fill in the notification title',
                  }}
                />

                {/* Message */}
                <div>
                  <Textarea<NotificationTextFields>
                    name="message"
                    control={textControl}
                    label="Message"
                    required
                    placeholder="Enter notification message"
                    rows={4}
                    maxLength={1000}
                    className="resize-none"
                    rules={{
                      validate: (value: string) => !!value?.trim() || 'Please fill in the notification message',
                      maxLength: { value: 1000, message: 'Message must be 1000 characters or less' },
                    }}
                  />
                  <p className={`mt-1 text-xs text-right ${messageLength > 900 ? 'text-amber-500' : 'text-gray-400'}`}>
                    {messageLength}/1000
                  </p>
                </div>
              </>
            )}

            {/* Type and Priority - SimpleSelect */}
            <div className="grid grid-cols-2 gap-4">
              <SimpleSelect
                label="Type"
                options={typeSelectOptions}
                value={formData.type}
                onChange={(val) =>
                  setFormData((prev) => ({
                    ...prev,
                    type: (val as NotificationType) || NotificationType.ANNOUNCEMENT,
                  }))
                }
                placeholder="Select type"
                isSearchable={false}
              />

              <SimpleSelect
                label="Priority"
                options={prioritySelectOptions}
                value={formData.priority}
                onChange={(val) =>
                  setFormData((prev) => ({
                    ...prev,
                    priority: (val as NotificationPriority) || NotificationPriority.NORMAL,
                  }))
                }
                placeholder="Select priority"
                isSearchable={false}
              />
            </div>

            {/* Channels */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Channels <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-3">
                {Object.values(NotificationChannel).map((channel) => (
                  <label
                    key={channel}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                      formData.channels.includes(channel)
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.channels.includes(channel)}
                      onChange={() => handleChannelToggle(channel)}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium">
                      {channel === 'IN_APP' ? 'In-App' : channel === 'PUSH' ? 'Push' : 'Email'}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Target Type — shared by both in-app and email. In email mode, selection drives the To field below. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Send To
              </label>
              <div className="grid grid-cols-4 gap-3">
                {[
                  { type: NotificationTargetType.ALL, icon: Globe, label: 'All Users' },
                  { type: NotificationTargetType.USER, icon: User, label: 'Specific Users' },
                  { type: NotificationTargetType.GROUP, icon: Users, label: 'Departments' },
                  { type: NotificationTargetType.ROLE, icon: Shield, label: 'Roles' },
                ].map(({ type, icon: Icon, label }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, target_type: type, target_ids: [] }));
                      // Any type change wipes the email "To" chips — they're always tied to one type.
                      setEmailRecipients([]);
                      // Reset the ROLE cascade state so the dept/role picker
                      // starts fresh if the admin flips back to it later.
                      setSelectedRoleDept('');
                      setSelectedRoleAssignments([]);
                    }}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-colors ${
                      formData.target_type === type
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-xs font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Target Selection - Cascading dept→role picker for ROLE target.
                Mirrors the CourseAssignment pattern: roles are nested under
                groups in the User model, so a role_key alone is ambiguous —
                we always send (group_key, role_key) tuples to the backend
                (see resolveTargetToUserIds in NotificationService). */}
            {!isEmailActive && formData.target_type === NotificationTargetType.ROLE && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Select Department & Role <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Department (group) select */}
                  <div>
                    <SimpleSelect
                      label="Department"
                      value={selectedRoleDept}
                      onChange={(value) => setSelectedRoleDept((value as string) || '')}
                      options={(groupsData || []).map((g: any) => ({
                        label: g.group_name || g.name,
                        value: g.group_key || g._id,
                      }))}
                      placeholder={isLoadingGroups ? 'Loading…' : 'Select department'}
                      isSearchable
                    />
                  </div>
                  {/* Role select — populated from /group-role-map/{dept}. On
                      pick, we append the (group_key, role_key) tuple to the
                      chip list below and clear the role select so the admin
                      can add more from the same dept without re-picking it. */}
                  <div>
                    <SimpleSelect
                      label="Role"
                      value=""
                      onChange={(value) => {
                        const role_key = (value as string) || '';
                        if (!role_key || !selectedRoleDept) return;
                        const role = (rolesForDept || []).find(
                          (r: any) => r.role_key === role_key
                        );
                        const group = (groupsData || []).find(
                          (g: any) => (g.group_key || g._id) === selectedRoleDept
                        );
                        const exists = selectedRoleAssignments.some(
                          (a) =>
                            a.group_key === selectedRoleDept &&
                            a.role_key === role_key
                        );
                        if (exists) return;
                        setSelectedRoleAssignments((prev) => [
                          ...prev,
                          {
                            group_key: selectedRoleDept,
                            role_key,
                            role_name: role?.role_name || role_key,
                            group_name: group?.group_name || group?.name || selectedRoleDept,
                          },
                        ]);
                      }}
                      options={(rolesForDept || []).map((r: any) => ({
                        label: r.role_name || r.role_key,
                        value: r.role_key,
                      }))}
                      placeholder={
                        !selectedRoleDept
                          ? 'Pick a department first'
                          : isLoadingRolesForDept
                          ? 'Loading…'
                          : (rolesForDept || []).length === 0
                          ? 'No roles in this department'
                          : 'Select role to add'
                      }
                      isSearchable
                      isDisabled={!selectedRoleDept || isLoadingRolesForDept}
                    />
                  </div>
                </div>

                {/* Chips for selected (dept, role) tuples */}
                {selectedRoleAssignments.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    {selectedRoleAssignments.map((a) => (
                      <span
                        key={`${a.group_key}::${a.role_key}`}
                        className="inline-flex items-center gap-2 px-2.5 py-1 text-xs font-medium bg-white border border-gray-300 rounded-full"
                      >
                        <span className="text-gray-500">{a.group_name}</span>
                        <span className="text-gray-300">/</span>
                        <span className="text-gray-900">{a.role_name}</span>
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedRoleAssignments((prev) =>
                              prev.filter(
                                (x) =>
                                  !(x.group_key === a.group_key && x.role_key === a.role_key)
                              )
                            )
                          }
                          className="text-gray-400 hover:text-red-600"
                          title="Remove"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  Notifications are fanned out to all active users that hold any of the selected role(s) in the selected department(s).
                </p>
              </div>
            )}

            {/* Target Selection - Search + Multi-select for USER / GROUP */}
            {!isEmailActive &&
              formData.target_type !== NotificationTargetType.ALL &&
              formData.target_type !== NotificationTargetType.ROLE && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select {targetLabel} <span className="text-red-500">*</span>
                </label>

                <ReactSelect<MultiSelectOption, true>
                  isMulti
                  options={targetOptions}
                  value={selectedTargetOptions}
                  onChange={(selected) => {
                    const ids = selected ? selected.map((s) => s.value) : [];
                    setFormData((prev) => ({ ...prev, target_ids: ids }));
                    // Cache selected user options so they survive search changes
                    if (formData.target_type === NotificationTargetType.USER && selected) {
                      setSelectedUserCache((prev) => {
                        const next = new Map(prev);
                        // Remove deselected
                        for (const key of prev.keys()) {
                          if (!ids.includes(key)) next.delete(key);
                        }
                        // Add newly selected
                        for (const s of selected) {
                          if (!next.has(s.value)) next.set(s.value, s);
                        }
                        return next;
                      });
                    }
                  }}
                  onInputChange={(inputValue, { action }) => {
                    if (formData.target_type === NotificationTargetType.USER) {
                      // Update search on typing AND on selection clear (set-value/menu-close)
                      // so the API re-fetches with the correct (possibly empty) search term.
                      if (action === 'input-change' || action === 'set-value' || action === 'menu-close') {
                        setUserSearch(inputValue);
                      }
                    }
                  }}
                  isLoading={isLoadingTarget}
                  isSearchable
                  filterOption={formData.target_type === NotificationTargetType.USER ? null : undefined}
                  placeholder={`Search and select ${targetLabel.toLowerCase()}...`}
                  noOptionsMessage={() =>
                    isLoadingTarget
                      ? 'Loading...'
                      : formData.target_type === NotificationTargetType.USER && !userSearch
                      ? 'Type to search users by name, email, or employee ID'
                      : `No ${targetLabel.toLowerCase()} found`
                  }
                  styles={multiSelectStyles}
                  classNamePrefix="react-select"
                  closeMenuOnSelect={false}
                  menuPlacement="auto"
                  maxMenuHeight={200}
                  menuPortalTarget={document.body}
                />
                {formData.target_ids.length > 0 && (
                  <p className="mt-2 text-xs text-gray-500">
                    {formData.target_ids.length} selected
                  </p>
                )}
              </div>
            )}

            {/* Email compose: To (filtered by target_type above) + Subject + CC + BCC + Body */}
            {isEmailActive && (
              <EmailComposePanel
                value={emailCompose}
                onChange={setEmailCompose}
                selectedRecipients={emailRecipients}
                onAddRecipient={handleAddRecipient}
                onRemoveRecipient={handleRemoveRecipient}
                targetType={formData.target_type as 'ALL' | 'USER' | 'GROUP' | 'ROLE'}
              />
            )}

            {/* Action URL - hidden when email channel is active */}
            {!isEmailActive && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Action URL <span className="text-gray-400">(optional)</span>
                </label>
                <div className="flex items-start gap-2">
                  <span className="flex items-center px-3 py-2 sm:py-3 bg-gray-50 text-xs sm:text-sm text-gray-500 border border-gray-200 rounded-lg sm:rounded-xl select-none whitespace-nowrap">
                    {window.location.origin}
                  </span>
                  <div className="flex-1 min-w-0">
                    <Input<NotificationTextFields>
                      name="action_url"
                      control={textControl}
                      type="text"
                      disableRTL
                      placeholder="/admin/notifications"
                      rules={{
                        validate: (value: string) =>
                          !value?.trim() || value.trim().startsWith('/') || 'Path must start with /',
                      }}
                    />
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Enter only the path. Leave blank to skip click navigation.
                </p>
              </div>
            )}

            {/* Push Options - shown only when PUSH channel is selected */}
            {formData.channels.includes(NotificationChannel.PUSH) && (
              <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg space-y-4">
                <div className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-blue-600" />
                  <h3 className="text-sm font-semibold text-gray-800">Push Customization</h3>
                  <span className="text-xs text-gray-500">(OneSignal)</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Icon — image preview + upload/replace/remove controls.
                      The raw S3 URL is intentionally not surfaced; admins
                      shouldn't be pasting URLs by hand here, just uploading
                      from disk. The icon also surfaces inline in the
                      notification list (see NotificationItem). */}
                  {(() => {
                    const url = pushOptions.icon_url || '';
                    const isUploading = uploadingField === 'icon_url';
                    return (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Icon
                        </label>
                        <div className="flex items-center gap-3">
                          {/* Preview tile — image when set, dashed placeholder otherwise */}
                          {url ? (
                            <div className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200 bg-white flex-shrink-0">
                              <img
                                src={url}
                                alt="Push icon preview"
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => setPushOptions((prev) => ({ ...prev, icon_url: '' }))}
                                title="Remove icon"
                                className="absolute top-1 right-1 p-1 bg-white/90 hover:bg-white text-gray-600 hover:text-red-600 rounded-full shadow-sm transition-colors"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <div className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 bg-white flex items-center justify-center flex-shrink-0">
                              <ImagePlus className="w-6 h-6 text-gray-400" />
                            </div>
                          )}

                          {/* Upload / Replace button */}
                          <label
                            className={`relative inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors ${
                              isUploading ? 'opacity-60 cursor-not-allowed' : ''
                            }`}
                            title={url ? 'Replace image' : 'Upload image'}
                          >
                            {isUploading ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                                <span>Uploading…</span>
                              </>
                            ) : (
                              <>
                                <ImagePlus className="w-4 h-4" />
                                <span>{url ? 'Replace' : 'Upload'}</span>
                              </>
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              disabled={isUploading}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) handlePushImageUpload('icon_url', f);
                                e.target.value = '';
                              }}
                              className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed"
                            />
                          </label>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          Small icon — usually the brand logo (recommended 256×256). Also shown in the notification list.
                        </p>
                      </div>
                    );
                  })()}

                  {/* TTL — OneSignal-style "value + unit" editor. The
                      number input lets the admin pick any positive integer
                      up to the per-unit maximum (28 days = 4 weeks = 672h =
                      40 320 min, dictated by FCM/WNS), and the unit
                      dropdown chooses the multiplier. We intercept
                      onChange and clamp BEFORE state is set, so the input
                      can never display a value above the limit. The
                      current limit is also shown beside the input so the
                      admin sees the cap without having to read the helper
                      text or guess from native browser tooltips. */}
                  <div>
                    <div className="flex items-baseline justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Time To Live
                      </label>
                      <span className="text-[11px] font-medium text-gray-500">
                        Max {maxValueForUnit(ttlUnit).toLocaleString()} {ttlUnit}
                      </span>
                    </div>
                    <div className="flex items-stretch gap-2">
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={Number.isFinite(ttlValue) ? ttlValue : ''}
                        onChange={(e) => {
                          // No clamping at the input layer — preserve the
                          // admin's exact keystrokes. validateTtl() in
                          // updateTtl decides whether to surface an inline
                          // error and whether to flow through to
                          // pushOptions.ttl. Empty input becomes NaN so
                          // validation can report "enter a number".
                          const raw = e.target.value;
                          const parsed = raw === '' ? NaN : parseInt(raw, 10);
                          updateTtl(parsed, ttlUnit);
                        }}
                        onKeyDown={(e) => {
                          // Block characters that can't yield a positive
                          // integer (decimal point, minus, plus, exponent).
                          // Backspace / arrows / digits still pass.
                          if (['.', '-', '+', 'e', 'E'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        className={`w-24 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                          ttlError
                            ? 'border-red-400 focus:ring-red-300'
                            : 'border-gray-300 focus:ring-primary-500'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <SimpleSelect
                          value={ttlUnit}
                          onChange={(value) =>
                            updateTtl(ttlValue, (value as TtlUnit) || 'days')
                          }
                          options={ttlUnitOptions}
                          isSearchable={false}
                        />
                      </div>
                    </div>
                    {ttlError ? (
                      <p className="mt-1 text-xs text-red-600 font-medium">
                        {ttlError}
                      </p>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">
                        How long the push service queues the notification if the device is offline. Max 28 days / 4 weeks
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Schedule Toggle */}
            {(
              <>
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isScheduled}
                      onChange={(e) => setIsScheduled(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">
                      Schedule for later
                    </span>
                  </div>
                </div>

                {/* Scheduled Time */}
                {isScheduled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Schedule Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      name="scheduled_at"
                      value={formData.scheduled_at}
                      onChange={handleChange}
                      min={new Date().toISOString().slice(0, 16)}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                        formErrors.scheduled_at
                          ? 'border-red-400 focus:ring-red-300'
                          : 'border-gray-200 focus:ring-primary-500'
                      }`}
                    />
                    {formErrors.scheduled_at && (
                      <p className="mt-1 text-xs text-red-600 font-medium">{formErrors.scheduled_at}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {isScheduled ? 'Scheduling...' : 'Sending...'}
                  </>
                ) : (
                  <>
                    {isScheduled ? <Calendar className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                    {isScheduled ? 'Schedule' : 'Send Now'}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SendNotificationModal;
